import type { CalendarDateTime, Time, ZonedDateTime } from "@internationalized/date";
import { TimeFieldInput, TimeFieldRoot } from "reka-ui";

type TimeValue = Time | CalendarDateTime | ZonedDateTime;
import { defineComponent } from "vue";
import { cn } from "@/utils/cn";
import { p } from "@/utils/props-builder";

const Root = defineComponent({
  name: "TimeField",
  props: {
    modelValue: p.custom<TimeValue | undefined>([Object]),
    defaultValue: p.custom<TimeValue | undefined>([Object]),
    placeholder: p.custom<TimeValue | undefined>([Object]),
    locale: p.string("en-US"),
    granularity: p.string<"hour" | "minute" | "second">("minute"),
    hourCycle: p.number<12 | 24>(),
    disabled: p.boolean(false),
    readonly: p.boolean(false),
    minValue: p.custom<TimeValue | undefined>([Object]),
    maxValue: p.custom<TimeValue | undefined>([Object]),
  },
  emits: {
    "update:modelValue": (_value: TimeValue) => true,
  },
  setup(props, { emit, attrs }) {
    return () => (
      <TimeFieldRoot
        class={cn(
          "border-input flex h-8 w-full items-center rounded-md border bg-transparent px-3 py-1 text-sm transition-colors",
          "hover:border-accent-10",
          "focus-within:border-accent-11",
          props.disabled && "cursor-not-allowed opacity-50",
          attrs.class as string
        )}
        modelValue={props.modelValue}
        defaultValue={props.defaultValue}
        placeholder={props.placeholder}
        locale={props.locale}
        granularity={props.granularity}
        hourCycle={props.hourCycle}
        disabled={props.disabled}
        readonly={props.readonly}
        minValue={props.minValue}
        maxValue={props.maxValue}
        onUpdate:modelValue={(value: TimeValue | undefined) => {
          if (value) emit("update:modelValue", value);
        }}
        v-slots={{
          default: ({ segments }: { segments: { part: string; value: string }[] }) => (
            <>
              {segments.map((segment, idx) =>
                segment.part === "literal" ? (
                  <span key={idx} class="text-muted-foreground">
                    {segment.value}
                  </span>
                ) : (
                  <TimeFieldInput
                    key={idx}
                    part={segment.part as "hour" | "minute" | "second" | "dayPeriod"}
                    class="focus:bg-accent-3 focus:text-accent-11 rounded-sm px-0.5 tabular-nums focus:outline-none"
                  >
                    {segment.value}
                  </TimeFieldInput>
                )
              )}
            </>
          ),
        }}
      />
    );
  },
});

export default {
  Root,
};
